import {Filter, Search} from 'lucide-react';
import {useMemo} from 'react';

import {sortEmployeesByName} from '../../../../utils';
import {BaselineCalendar} from '../BaselineCalendar';
import {LocationProgressBar} from '../LocationProgressBar';
import {SortDropdown} from '../SortDropdown';
import {EmployeeRow} from './EmployeeRow';

const getCompanyStats = (employees) => {
  let completed_count = 0;
  let in_progress_count = 0;
  let not_started_count = 0;

  let total_completed = 0;
  let total_required = 0;

  employees.forEach((emp) => {
    total_completed +=
      emp.total_completed > emp.total_required
        ? emp.total_required
        : emp.total_completed;
    total_required += emp.total_required;

    switch (emp.status) {
      case 'completed':
        completed_count += 1;
        break;
      case 'in_progress':
        in_progress_count += 1;
        break;
      case 'not_started':
        not_started_count += 1;
        break;
      default:
        break;
    }
  });

  return {
    completed_count,
    in_progress_count,
    not_started_count,
    total_completed,
    total_required,
  };
};

export const BaselineManagerView = ({
  data,
  orgId,
  searchTerm,
  setSearchTerm,
  sortBy,
  setSortBy,
  filterStatus,
  setFilterStatus,
  tempFilterStatus,
  setTempFilterStatus,
  showSortDropdown,
  setShowSortDropdown,
  showFilterDropdown,
  setShowFilterDropdown,
  getStatusLabel,
}) => {
  const {
    total_required,
    not_started_count,
    in_progress_count,
    completed_count,
    total_completed,
  } = getCompanyStats(data);

  const filteredAndSortedData = useMemo(() => {
    let filtered = data;

    if (searchTerm) {
      filtered = data.filter(
        (emp) =>
          `${emp.first_name} ${emp.last_name}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          emp.uid.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter((emp) => emp.status === filterStatus);
    }

    if (sortBy === 'alphabetical') {
      return sortEmployeesByName(filtered);
    }

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'least_completed': {
          const aPercentage =
            a.total_required > 0
              ? (a.total_completed / a.total_required) * 100
              : 0;
          const bPercentage =
            b.total_required > 0
              ? (b.total_completed / b.total_required) * 100
              : 0;
          return aPercentage - bPercentage;
        }
        case 'most_completed': {
          const aPercentageMost =
            a.total_required > 0
              ? (a.total_completed / a.total_required) * 100
              : 0;
          const bPercentageMost =
            b.total_required > 0
              ? (b.total_completed / b.total_required) * 100
              : 0;
          return bPercentageMost - aPercentageMost;
        }
        default:
          return 0;
      }
    });
  }, [data, searchTerm, filterStatus, sortBy]);

  const groupedData = useMemo(() => {
    if (sortBy === 'alphabetical') {
      const grouped = {};

      filteredAndSortedData.forEach((employee) => {
        const firstLetter = employee.first_name.charAt(0).toUpperCase();
        if (!grouped[firstLetter]) {
          grouped[firstLetter] = [];
        }
        grouped[firstLetter].push(employee);
      });

      const sortedLetters = Object.keys(grouped).sort();

      return sortedLetters.map((letter) => ({
        letter,
        items: grouped[letter],
      }));
    } else {
      return [
        {
          letter: '',
          items: filteredAndSortedData,
        },
      ];
    }
  }, [filteredAndSortedData, sortBy]);

  const handleFilterDropdownToggle = () => {
    if (!showFilterDropdown) {
      setTempFilterStatus(filterStatus);
      setShowSortDropdown(false);
    }
    setShowFilterDropdown(!showFilterDropdown);
  };

  const handleSortDropdownToggle = () => {
    setShowFilterDropdown(false);
    setShowSortDropdown(!showSortDropdown);
  };

  const handleApplyFilter = () => {
    setFilterStatus(tempFilterStatus);
    setShowFilterDropdown(false);
  };

  const handleResetFilter = () => {
    setTempFilterStatus('all');
  };

  const handleCancelFilter = () => {
    setTempFilterStatus(filterStatus);
    setShowFilterDropdown(false);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setShowSortDropdown(false);
  };

  return (
    <div className='baseline-container'>
      <div className='baseline-wrapper'>
        <div className='baseline-card'>
          <div className='baseline-header'>
            <h1 className='baseline-title'>Baseline Creation Compliance</h1>
          </div>

          <div className='baseline-content baseline-subheader'>
            <div>
              <div className='baseline-location-name'>
                <h2 className='baseline-title baseline-title-2'>
                  Team Progress Snapshot
                </h2>
                <div className='baseline-location-stats'>
                  <span className='baseline-stat-item completed'>
                    Completed: {completed_count}
                  </span>
                  <span className='baseline-stat-item in-progress'>
                    In Progress: {in_progress_count}
                  </span>
                  <span className='baseline-stat-item not-started'>
                    Not Started: {not_started_count}
                  </span>
                </div>
              </div>
              <div className='baseline-location-progress'>
                <LocationProgressBar
                  completed={total_completed}
                  total={total_required}
                />
              </div>
            </div>
          </div>

          <div className='baseline-content'>
            <div className='baseline-controls baseline-controls-manager'>
              <div className='baseline-search-container'>
                <Search className='baseline-search-icon' />
                <input
                  type='text'
                  placeholder='Search employee....'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className='baseline-search-input'
                />
              </div>

              <div className='baseline-button-group'>
                <div className='baseline-dropdown-container'>
                  <button
                    onClick={handleFilterDropdownToggle}
                    className='baseline-button'
                  >
                    <Filter className='baseline-button-icon' />
                    Filter
                  </button>
                  {showFilterDropdown && (
                    <div className='baseline-dropdown baseline-dropdown-filter'>
                      <div className='baseline-dropdown-content'>
                        <div className='baseline-dropdown-title'>
                          Employee Baseline Progress
                        </div>
                        <div className='baseline-dropdown-options'>
                          {[
                            'all',
                            'not_started',
                            'in_progress',
                            'completed',
                          ].map((status) => (
                            <label
                              key={status}
                              className='baseline-dropdown-option'
                            >
                              <input
                                type='radio'
                                name='filter'
                                value={status}
                                checked={tempFilterStatus === status}
                                onChange={(e) =>
                                  setTempFilterStatus(e.target.value)
                                }
                              />
                              <span>
                                {status === 'all'
                                  ? 'All'
                                  : getStatusLabel(status)}
                              </span>
                            </label>
                          ))}
                        </div>
                        <div className='baseline-dropdown-actions'>
                          <button
                            onClick={handleApplyFilter}
                            className='baseline-dropdown-btn baseline-dropdown-btn-apply'
                          >
                            Apply
                          </button>
                          <button
                            onClick={handleResetFilter}
                            className='baseline-dropdown-btn baseline-dropdown-btn-reset'
                          >
                            Reset
                          </button>
                          <button
                            onClick={handleCancelFilter}
                            className='baseline-dropdown-btn baseline-dropdown-btn-cancel'
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <SortDropdown
                  isAdmin={false}
                  showDropdown={showSortDropdown}
                  sortBy={sortBy}
                  onToggle={handleSortDropdownToggle}
                  onSortChange={handleSortChange}
                />
              </div>
            </div>
            {filteredAndSortedData.length === 0 ? (
              <div className='baseline-empty-state'>
                <div className='baseline-empty-text'>No employees found</div>
                {(searchTerm || filterStatus !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilterStatus('all');
                    }}
                    className='baseline-clear-filters'
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div className='baseline-employee-list'>
                {groupedData.map((group) => (
                  <div key={group.letter}>
                    {group.items.map((item, index) => (
                      <EmployeeRow
                        key={item.employee_id}
                        item={item}
                        index={index}
                        groupLetter={group.letter}
                        sortBy={sortBy}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
          {filteredAndSortedData.length > 0 && (
            <BaselineCalendar orgId={orgId} />
          )}
        </div>
      </div>
    </div>
  );
};
